<template>
  <div id="app">
    <NavBar v-if="$route.name !== 'Login'" />
    <div
      v-if="authToken || $route.name === 'Login'"
      :class="$route.name !== 'Login' ? 'main-content flex-column' : 'main-content-login flex-column'"
    >
      <div class="placeholder-name flex-row">
        <router-view />
      </div>
      <AppFooter></AppFooter>
    </div>
    <div v-if="filterOpen" class="opaque-bg"></div>
    <VueFlyout width="100%" group="primary" position="bottom left" :duration="10000" animation-name="notification">
      <template v-slot:body="props">
        <div class="lxp-notification" :class="props.item.type" @click="props.close">
          <IconNotificationError v-if="props.item.type === 'error'" class="icon" />
          <IconNotificationSuccess v-else-if="props.item.type === 'success'" class="icon" />
          <IconNotificationPending v-else-if="props.item.type === 'pending'" class="icon" />
          <IconNotificationPoints v-else-if="props.item.type === 'points'" class="icon" />
          <template v-else-if="props.item.type === 'badge'">
            <ImageBadge class="icon" v-bind="{ ...props.item.text }" />
          </template>
          <!-- vue-notifications doens't let you add custom keys apparently so I had to add extras to text as an object -->
          <!-- To stay backwards compatible I check for string or object -->
          <span class="text--flyout">
            {{ typeof props.item.text === 'string' ? props.item.text : props.item.text.message }}
          </span>
        </div>
      </template>
    </VueFlyout>
  </div>
</template>

<script>
import AppFooter from '@/components/AppFooter.vue';
import NavBar from '@/components/NavBar.vue';

import IconNotificationError from '@/assets/new/notification_error.svg';
import IconNotificationSuccess from '@/assets/new/notification_success.svg';
import IconNotificationPoints from '@/assets/new/notification_points.svg';
import IconNotificationPending from '@/assets/new/classrooms_info_icon.svg';

export default {
  name: 'App',
  components: {
    AppFooter,
    NavBar,
    IconNotificationError,
    IconNotificationSuccess,
    IconNotificationPoints,
    IconNotificationPending,
  },
  data() {
    return {
      loader: this.$loading.show(),
      domains: null,
      navItems: [],
      loginType: null,
    };
  },
  methods: {
    getNavPermissions() {
      this.$store
        .dispatch('global/getNavPermissions', { domain: this.loginInfo.domain })
        .then((res) => {
          this.$store.state.global.navPermissions = res;
          this.assignRole();
          this.navigateUser();
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          this.loader.hide();
        });
    },
    assignRole() {
      if (this.loginInfo.entity && this.loginInfo.entity !== 'NA' && this.loginInfo.domain) {
        this.$store.state.userType = 'Dealer';
        if (
          this.loginInfo.roles[0] &&
          (this.loginInfo.roles[0].roleDetails.name === 'sales-am' ||
            this.loginInfo.roles[0].roleDetails.name === 'service-am')
        ) {
          this.$store.state.userType = 'Field User';
        }
      } else {
        this.$store.state.userType = 'Field User';
      }
    },
    navigateUser() {
      if (this.$store.state.userType === 'Field User') {
        const providedDomain = !this.isEmpty(this.$route.params);
        if (providedDomain) {
          return;
        }
        this.$router.push({
          name: 'Programs',
        });
      } else if (this.loginType === 'token' && this.$store.state.userType === 'Dealer') {
        const { domain, entity } = this.loginInfo;
        this.$router.push({
          path: `/dealer/${domain}/${entity}/dashboard`,
        });
      } else if (this.$route.name !== 'Dashboard') {
        this.$router.push({
          name: 'Dealer',
        });
      }
    },
    isEmpty(obj) {
      if (obj) {
        return Object.keys(obj).length === 0;
      }
      return false;
    },
  },
  computed: {
    filterOpen() {
      if (this.$store.state.filterModalOpen) {
        return this.$store.state.filterModalOpen;
      }
      return false;
    },
    authToken() {
      return this.$store.state.login.token;
    },
    loginInfo() {
      return this.$store.state.login.loginInfo;
    },
  },
  created() {
    // Hide App loader if user is on custom login.
    if (window.location.pathname === '/login') {
      this.loader.hide();
      return;
    }
    // If the user has a JWT token in the url from Stellantis link, use it to log in the user.
    // If the user has a JWT token in local storage, use it to authenticate the user.
    // Else Reroute the user back to Stellantis or the custom login page depending on the environment.

    if (this.$route.query.token) {
      this.$store
        .dispatch('getLoginInfo', this.$route.query.token)
        .then(() => {
          this.loginType = 'token';
          this.getNavPermissions();
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        });
    } else if (localStorage.getItem('stellantis-user')) {
      this.$store.dispatch('checkStorageAuth', JSON.parse(localStorage.getItem('stellantis-user')));
      this.loginType = 'local';
      this.getNavPermissions();
    } else if (process.env.VUE_APP_DEPLOY_ENV === 'qa') {
      window.location = 'https://stagdealerconnect.extra.chrysler.com';
    } else if (process.env.VUE_APP_DEPLOY_ENV === 'prod') {
      window.location = 'https://dealerconnect.chrysler.com';
    } else {
      this.loader.hide();
      this.$router.push({
        name: 'Login',
      });
    }
  },
};
</script>

<style lang="scss">
@import './assets/scss/base.scss';
@import './assets/scss/programStyle.scss';
@import './assets/fonts/fonts.css';

#app {
  font-family: 'Encode Sans', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: $primary;
  background-color: white;
  margin: 0 auto;
  justify-content: space-between;
  display: flex;
  width: 100%;
  flex-direction: row;
  height: 100%;
  overflow: hidden;
}
.main-content {
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}
.main-content-login {
  width: 100%;
  justify-content: space-between;
  align-items: center;
}
#nav {
  padding: 30px;

  a {
    font-weight: bold;
    color: #2c3e50;

    &.router-link-exact-active {
      color: #42b983;
    }
  }
}
.opaque-bg {
  height: 100%;
  width: 100%;
  position: absolute;
  background-color: black;
  opacity: 0.6;
}
</style>
